import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Bell, ChevronLeft, Loader2, Shield, Plus, Trash2, 
  AlertTriangle, Info, Wrench, Home
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import adminApi from "../../lib/adminApi";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../../components/ui/dialog";

const ADMIN_EMAIL = "cpt.serdarakdemir@gmail.com";

const TYPE_CONFIG = {
  info: { icon: Info, color: "text-[#4DFFFF]", bg: "bg-[#4DFFFF]/20" },
  warning: { icon: AlertTriangle, color: "text-yellow-400", bg: "bg-yellow-400/20" },
  maintenance: { icon: Wrench, color: "text-red-400", bg: "bg-red-400/20" },
};

export default function AdminAnnouncements() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: "",
    content: "",
    type: "info",
    active: true
  });

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await adminApi.getAnnouncements();
      setAnnouncements(response.data);
    } catch (error) {
      console.error("Announcements error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newAnnouncement.title || !newAnnouncement.content) {
      toast.error("Başlık ve içerik gerekli");
      return;
    }
    
    setCreating(true);
    try {
      await adminApi.createAnnouncement(newAnnouncement);
      toast.success("Duyuru oluşturuldu");
      setShowCreateDialog(false);
      setNewAnnouncement({ title: "", content: "", type: "info", active: true });
      fetchAnnouncements();
    } catch (error) {
      toast.error("Oluşturma başarısız");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminApi.deleteAnnouncement(id);
      toast.success("Duyuru silindi");
      fetchAnnouncements();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  // Check admin access
  if (user?.email !== ADMIN_EMAIL) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Yetkisiz Erişim</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-announcements">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/dashboard" className="p-2 rounded-lg bg-[#4DFFFF]/20 hover:bg-[#4DFFFF]/30 text-[#4DFFFF]" title="Ana Sayfa">
            <Home className="w-5 h-5" />
          </Link>
          <Link to="/admin" className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white" title="Admin Panel">
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-3">
            <Bell className="w-6 h-6 text-[#22C55E]" />
            <h1 className="text-2xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
              Duyurular
            </h1>
          </div>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="bg-[#4DFFFF] text-[#050E2E]">
          <Plus className="w-4 h-4 mr-2" />
          Yeni Duyuru
        </Button>
      </div>

      {/* Announcements List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#4DFFFF]" />
        </div>
      ) : announcements.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <Bell className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
          <p className="text-[#B1C1C1]">Henüz duyuru yok</p>
        </div>
      ) : (
        <div className="space-y-4">
          {announcements.map((announcement) => {
            const config = TYPE_CONFIG[announcement.type] || TYPE_CONFIG.info;
            const Icon = config.icon;
            
            return (
              <div key={announcement.announcement_id} className="glass-card p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${config.bg}`}>
                      <Icon className={`w-5 h-5 ${config.color}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-white font-medium">{announcement.title}</h3>
                        {!announcement.active && (
                          <span className="px-2 py-0.5 rounded text-xs bg-white/10 text-[#64748B]">Pasif</span>
                        )}
                      </div>
                      <p className="text-sm text-[#B1C1C1] mb-2">{announcement.content}</p>
                      <p className="text-xs text-[#64748B]">
                        {new Date(announcement.created_at).toLocaleString("tr-TR")}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(announcement.announcement_id)}
                    className="p-2 rounded-lg hover:bg-red-500/20 text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-[#0A1635] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Yeni Duyuru</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-[#B1C1C1]">Başlık</label>
              <Input
                value={newAnnouncement.title}
                onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                placeholder="Duyuru başlığı"
                className="mt-1 bg-[#050E2E]/50 border-white/10 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-[#B1C1C1]">İçerik</label>
              <textarea
                value={newAnnouncement.content}
                onChange={(e) => setNewAnnouncement({ ...newAnnouncement, content: e.target.value })}
                placeholder="Duyuru içeriği"
                rows={4}
                className="w-full mt-1 px-4 py-2 rounded-lg bg-[#050E2E]/50 border border-white/10 text-white resize-none"
              />
            </div>
            <div>
              <label className="text-sm text-[#B1C1C1]">Tip</label>
              <select
                value={newAnnouncement.type}
                onChange={(e) => setNewAnnouncement({ ...newAnnouncement, type: e.target.value })}
                className="w-full mt-1 px-4 py-2 rounded-lg bg-[#050E2E]/50 border border-white/10 text-white"
              >
                <option value="info">Bilgi</option>
                <option value="warning">Uyarı</option>
                <option value="maintenance">Bakım</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreateDialog(false)}>İptal</Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-[#4DFFFF] text-[#050E2E]">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Oluştur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
